/* eslint-disable object-curly-newline */
import _ from 'underscore';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  TilesGridCellImage as CellImage,
  TilesGridCellDecorativeLabel as CellLabel,
  TextWithHighlight,
  TilesGridCellCardWithOverlay,
  TilesGridCellSelectionDecorator,
  TilesGridCellDeletionButtonDecorator,
} from '@bingads-webui-campaign-react/asset-library-components';
import { CSMLAssetSource, getTimeDescription } from '@bingads-webui-campaign-react/asset-library-model';
import { CustomFluentUIIcons } from '@bingads-webui-campaign-react/asset-library-resources';

import { getImageAssetsGridCellStyle } from './styles/image-assets-grid-cell-style';

const ImageAssetsGridCell = ({
  item,
  highlightedText,
  isOverlayEnabled,
  isCellFocused,
  renderCellContent,
  renderOverlayContent,
  i18n,
  selectable,
  selected,
  isMultiSelecting, // is in multi-selection mode, need to show checkbox on cells
  onClick,
  deletable,
  onDelete,
  classes,
}) => {
  //
  const [isImageLoaded, setImageLoaded] = useState(false);

  const { name, url, id, createdTime, updating, performanceLabel, sourceType } = item || {};
  const timeDesc = useMemo(() => getTimeDescription(createdTime, i18n), [createdTime, i18n]);

  const perfLabelProps = useMemo(() => {
    const labelProps = {
      best: {
        text: i18n.getString(_TL_('Top-rated')),
        classes: { iconTextWrapper: 'perf-label-icon-and-text-top-rated' },
      },
    };

    return performanceLabel && labelProps[performanceLabel.toLowerCase()];
  }, [i18n, performanceLabel]);

  const cellContent = useMemo(() => {
    const defaultContent = (
      <Fragment>
        <CellImage
          src={url}
          alt={name || id || ''}
          showSpinner={false}
          onLoaded={setImageLoaded}
          i18n={i18n}
        />
        <div className={classes.imageCellLabelContainer}>
          {(sourceType === CSMLAssetSource.AIGenerated ||
          sourceType === CSMLAssetSource.BackgroundChangeGenerated ||
          sourceType === CSMLAssetSource.AIGeneratedByRecraft) && (
            <CellLabel
              iconName={CustomFluentUIIcons.SparkeSVGIcon}
              classes={{ iconTextWrapper: 'created-with-ai-label' }}
            />
          )}
          {updating && (
            <CellLabel
              text={i18n.getString(_TL_('Updating'))}
              iconName="Sync"
              classes={{ iconTextWrapper: 'updating-label-icon-and-text' }}
            />
          )}
          {!updating && isImageLoaded && timeDesc && (
            <CellLabel
              text={i18n.getString(_TL_('New'))}
              secondaryText={timeDesc}
              iconName="AlertSolid"
            />
          )}
          {/* don't show the label if there is a time desc, which means the perf lable
              is only shown after 7 days since an image is added into asset libary */}
          { perfLabelProps && !updating && !timeDesc && isImageLoaded && (
            <CellLabel
              iconName="Calories"
              {...perfLabelProps}
            />
          )}
        </div>
      </Fragment>
    );
    if (renderCellContent) {
      return renderCellContent(defaultContent);
    }
    return defaultContent;
  }, [classes.imageCellLabelContainer, i18n, id, isImageLoaded, name, perfLabelProps, renderCellContent, sourceType, timeDesc, updating, url]);

  const funcRenderOverlayContent = useCallback(() => {
    if (renderOverlayContent) {
      return renderOverlayContent({ newLabel: timeDesc });
    }
    return null;
  }, [renderOverlayContent, timeDesc]);

  const onCellClick = useCallback(() => {
    if (selectable) {
      onClick({ item, selected });
    }
  }, [item, onClick, selectable, selected]);

  const withSelectionDecorator = useCallback(decoratedContent => (
    selectable ? (
      <TilesGridCellSelectionDecorator item={item} selected={selected} onClick={onClick} ariaLabel={name || i18n.getString(_TL_('Image'))} isFocused={isCellFocused} >
        {decoratedContent}
      </TilesGridCellSelectionDecorator>
    ) : decoratedContent
  ), [i18n, isCellFocused, item, name, onClick, selectable, selected]);

  const withDeleteButtonDecorator = useCallback(decoratedContent => (
    deletable ? (
      <TilesGridCellDeletionButtonDecorator item={item} onDelete={onDelete}>
        {decoratedContent}
      </TilesGridCellDeletionButtonDecorator>
    ) : decoratedContent
  ), [deletable, item, onDelete]);

  return (
    <div className={classnames('tiles-grid-cell-content', classes.imageAssetsGridCellContainer)}>
      <TilesGridCellCardWithOverlay
        isCellFocused={isCellFocused}
        isOverlayEnabled={isOverlayEnabled && isImageLoaded}
        renderOverlayContent={funcRenderOverlayContent}
        customCellCardClass={(isOverlayEnabled && !selected && !isMultiSelecting) ? classes.selectableCardContainerAutoHideCheckbox : classes.selectableCardContainer}
        onClick={onCellClick}
      >
        {_.compose(withDeleteButtonDecorator, withSelectionDecorator)(cellContent)}
      </TilesGridCellCardWithOverlay>
      <div className="cell-description">
        <div className="cell-description-name">
          <TextWithHighlight text={name || ''} highlightedText={highlightedText} enableTooltip />
        </div>
        {id && (
          <div className="cell-description-id">
            {`${i18n.getString(_TL_('ID'))} `}
            <TextWithHighlight text={id} highlightedText={highlightedText} />
          </div>
        )}
      </div>
    </div>
  );
};

ImageAssetsGridCell.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
    // cropping task progress
    updating: PropTypes.bool,
  }).isRequired,
  highlightedText: PropTypes.string,
  isOverlayEnabled: PropTypes.bool,
  isCellFocused: PropTypes.bool,
  renderCellContent: PropTypes.func,
  renderOverlayContent: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: () => null,
  isMultiSelecting: PropTypes.bool,
  deletable: PropTypes.bool,
  onDelete: PropTypes.func,

  // injected styles
  classes: PropTypes.shape({
    imageAssetsGridCellContainer: PropTypes.string,
    selectableCardContainer: PropTypes.string,
    selectableCardContainerAutoHideCheckbox: PropTypes.string,
  }).isRequired,
};

ImageAssetsGridCell.defaultProps = {
  highlightedText: undefined,
  isOverlayEnabled: false,
  isCellFocused: false,
  renderCellContent: null,
  renderOverlayContent: null,
  selectable: false,
  selected: false,
  onClick: undefined,
  isMultiSelecting: false,
  deletable: false,
  onDelete: undefined,
};

export const AssetLibraryImageAssetsGridCellBase
  = withDefaultStyles(ImageAssetsGridCell, getImageAssetsGridCellStyle);
